import type { ReactNode } from "react";
import type { EnrichmentDraft, EnrichmentKind } from "./enrichment-copy";
import { CONNECTOR_TYPE_FOR_KIND, KIND_LABEL, QUESTION, SOURCE_KEY_SUGGESTIONS } from "./enrichment-copy";
import type { EnrichmentConnection } from "./EnrichmentsField";
import { DiscoverInput } from "./DiscoverInput";

const SUGGESTIONS_ID = "enrich-source-key-suggestions";

export function EnrichmentEditor({
  draft,
  connections,
  discover,
  errors,
  index,
  onChange,
  onDone,
}: {
  draft: EnrichmentDraft;
  connections: EnrichmentConnection[];
  discover?: (instanceId: string, kind: string) => Promise<{ items: { id: string; name: string }[] }>;
  errors?: Record<string, string>;
  index: number;
  onChange: (next: EnrichmentDraft) => void;
  onDone: () => void;
}) {
  function set<K extends keyof EnrichmentDraft>(key: K, val: EnrichmentDraft[K]) {
    onChange({ ...draft, [key]: val });
  }
  function pickKind(kind: EnrichmentKind) {
    // Reset the connection when the kind changes — the old one is the wrong type.
    onChange({ ...draft, kind, instanceId: undefined });
  }
  const err = (field: string) => errors?.[`enrichments.${index}.${field}`];
  const matching = connections.filter((c) => c.connectorType === CONNECTOR_TYPE_FOR_KIND[draft.kind]);

  return (
    <div>
      <Q label={QUESTION.outputField} error={err("outputField")}>
        <input className="field__input" aria-label={QUESTION.outputField} value={draft.outputField ?? ""} onChange={(e) => set("outputField", e.target.value)} />
      </Q>

      <div className="enrich__q">
        <div className="enrich__q-label">{QUESTION.kind}</div>
        <div className="enrich__kind">
          {(["snowflake-view", "profile-field"] as EnrichmentKind[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`enrich__kind-btn${draft.kind === k ? " is-active" : ""}`}
              onClick={() => pickKind(k)}
            >
              {k === "snowflake-view" ? "📊 " : "👤 "}{KIND_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      <Q label={QUESTION.instanceId} error={err("instanceId")}>
        <select className="field__input" aria-label={QUESTION.instanceId} value={draft.instanceId ?? ""} onChange={(e) => set("instanceId", e.target.value)}>
          <option value="">—</option>
          {matching.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </Q>

      <Q label={QUESTION.sourceKeyPath} error={err("sourceKeyPath")}>
        <input className="field__input" aria-label={QUESTION.sourceKeyPath} list={SUGGESTIONS_ID} value={draft.sourceKeyPath ?? ""} onChange={(e) => set("sourceKeyPath", e.target.value)} />
        <datalist id={SUGGESTIONS_ID}>
          {SOURCE_KEY_SUGGESTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </datalist>
      </Q>

      {draft.kind === "snowflake-view" ? (
        <>
          <Q label={QUESTION.view} error={err("view")}>
            <DiscoverInput ariaLabel={QUESTION.view} instanceId={draft.instanceId} discoverKind="view" discover={discover} value={draft.view ?? ""} onChange={(v) => set("view", v)} />
          </Q>
          <Q label={QUESTION.keyColumn} error={err("keyColumn")}>
            <DiscoverInput ariaLabel={QUESTION.keyColumn} instanceId={draft.instanceId} discoverKind={draft.view ? `column:${draft.view}` : "column"} discover={discover} value={draft.keyColumn ?? ""} onChange={(v) => set("keyColumn", v)} />
          </Q>
          <Q label={QUESTION.valueColumn} error={err("valueColumn")}>
            <DiscoverInput ariaLabel={QUESTION.valueColumn} instanceId={draft.instanceId} discoverKind={draft.view ? `column:${draft.view}` : "column"} discover={discover} value={draft.valueColumn ?? ""} onChange={(v) => set("valueColumn", v)} />
          </Q>
          <Q label={QUESTION.keyType} error={err("keyType")}>
            <select
              className="field__input"
              aria-label={QUESTION.keyType}
              value={draft.keyType ?? "string"}
              onChange={(e) => set("keyType", e.target.value as "string" | "number")}
            >
              <option value="string">No — text</option>
              <option value="number">Yes — a number</option>
            </select>
          </Q>
        </>
      ) : (
        <Q label={QUESTION.profileField} error={err("profileField")}>
          <DiscoverInput ariaLabel={QUESTION.profileField} instanceId={draft.instanceId} discoverKind="field" discover={discover} value={draft.profileField ?? ""} onChange={(v) => set("profileField", v)} />
        </Q>
      )}

      <label className="enrich__q" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {/* No aria-label — the wrapping <label> text (QUESTION.required) is the accessible name. */}
        <input type="checkbox" checked={Boolean(draft.required)} onChange={(e) => set("required", e.target.checked)} />
        <span>{QUESTION.required}</span>
      </label>

      <button type="button" className="btn btn--secondary" onClick={onDone}>Done</button>
    </div>
  );
}

function Q({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="enrich__q">
      <div className="enrich__q-label">{label}</div>
      {children}
      {error && <div className="field__error">{error}</div>}
    </div>
  );
}
